/**
 * Collect form values in DOCX-specific format
 * Groups values by row ID with downloadItems array
 */
export interface DocxDownloadItem {
  itemId: string;
  value: string | boolean | string[];
}

export interface DocxRow {
  rowId: string;
  downloadItems: DocxDownloadItem[];
}

export interface DocxFormData {
  rows: DocxRow[];
}

export interface DocxTableInfo {
  tableId: string;
  rows: DocxRow[];
}

export interface DocxFormSaveValues {
  tableInfo: DocxTableInfo[];
}

export function collectDocxFormValues(
  containerSelector: string = ".preview-form-content"
): DocxFormData {
  const container = document.querySelector(containerSelector);
  if (!container) {
    console.warn("collectDocxFormValues: Container not found");
    return { rows: [] };
  }

  const rows: DocxRow[] = [];

  // Find all grid rows with data-row-id attribute (now divs instead of tr)
  const gridRows = container.querySelectorAll("div[data-row-id]");

  console.log("Collecting DOCX form values from", gridRows.length, "rows");

  gridRows.forEach((rowElement) => {
    const rowId = rowElement.getAttribute("data-row-id");
    if (!rowId) return;

    const dataType = rowElement.querySelector("div[data-type]");
    const downloadItems: DocxDownloadItem[] = [];

    let inputs: NodeListOf<Element>;
    if (dataType == null) {
      inputs = rowElement.querySelectorAll("input, textarea, select");

      inputs.forEach((el) => {
        const dataId = (el as HTMLElement).getAttribute("data-id");
        if (!dataId) return;
        let value: string | boolean = "";

        if (el instanceof HTMLInputElement) {
          if (el.type === "checkbox") {
            value = el.checked;
          } else if (el.type === "radio") {
            value = el.checked;
          } else {
            value = el.value;
          }
        } else if (el instanceof HTMLTextAreaElement) {
          value = el.value;
        } else if (el instanceof HTMLSelectElement) {
          value = el.value;
        }

        downloadItems.push({
          itemId: dataId,
          value: value,
        });
      });
    } else {
      const dataId = (dataType as HTMLElement).getAttribute("data-id");
      if (!dataId) return;
      inputs = dataType.querySelectorAll("input");
      const value1: string[] = [];
      inputs.forEach((el) => {
        if (el instanceof HTMLInputElement) {
          value1.push(el.value);
        }
      });
      downloadItems.push({
        itemId: dataId,
        value: value1,
      });
    }

    // Find all input elements within this row (excluding labels)

    // Only add row if it has items
    if (downloadItems.length > 0) {
      rows.push({
        rowId,
        downloadItems,
      });
    }
  });

  console.log("Collected DOCX form data:", rows);
  return { rows };
}

/**
 * Collect form values grouped by table ID for save operation
 */
export function collectDocxFormValuesForSave(
  containerSelector: string = ".preview-form-content"
): DocxFormSaveValues {
  const container = document.querySelector(containerSelector);
  if (!container) {
    console.warn("collectDocxFormValuesForSave: Container not found");
    return { tableInfo: [] };
  }
  const tableMap = new Map<string, DocxRow[]>();

  // Find all tables with data-table-id
  const tables = container.querySelectorAll("div[data-table-id]");

  console.log("Collecting DOCX form values from", tables.length, "tables");

  tables.forEach((tableElement) => {
    const tableId = tableElement.getAttribute("data-table-id");
    if (!tableId) return;

    const tableRows: DocxRow[] = [];

    // Find all rows within this table
    const gridRows = tableElement.querySelectorAll("div[data-row-id]");

    gridRows.forEach((rowElement) => {
      const rowId = rowElement.getAttribute("data-row-id");
      if (!rowId) return;

      const dataType = rowElement.querySelector("div[data-type]");

      const downloadItems: DocxDownloadItem[] = [];
      let inputs: NodeListOf<Element>;
      if (dataType == null) {
        inputs = rowElement.querySelectorAll("input, textarea, select");
        inputs.forEach((el) => {
          const dataId = (el as HTMLElement).getAttribute("data-id");
          if (!dataId) return;

          let value: string | boolean = "";

          if (el instanceof HTMLInputElement) {
            if (el.type === "checkbox") {
              value = el.checked;
            } else if (el.type === "radio") {
              value = el.checked;
            } else {
              value = el.value;
            }
          } else if (el instanceof HTMLTextAreaElement) {
            value = el.value;
          } else if (el instanceof HTMLSelectElement) {
            value = el.value;
          }

          downloadItems.push({
            itemId: dataId,
            value: value,
          });
        });
      } else {
        const dataId = (dataType as HTMLElement).getAttribute("data-id");
        if (!dataId) return;
        inputs = dataType.querySelectorAll("input");
        const value1: string[] = [];
        inputs.forEach((el) => {
          if (el instanceof HTMLInputElement) {
            value1.push(el.value);
          }
        });
        downloadItems.push({
          itemId: dataId,
          value: value1,
        });
      }

      // Find all input elements within this row

      // Only add row if it has items
      if (downloadItems.length > 0) {
        tableRows.push({
          rowId,
          downloadItems,
        });
      }
    });

    if (tableRows.length > 0) {
      tableMap.set(tableId, tableRows);
    }
  });

  const tableInfo: DocxTableInfo[] = Array.from(tableMap.entries()).map(
    ([tableId, tableRows]) => ({
      tableId: tableId,
      rows: tableRows,
    })
  );

  console.log("Collected DOCX form data grouped by table:", tableInfo);
  return { tableInfo };
}
